import { useCallback, useMemo, useRef } from "react";
import { AxiosResponseHeaders } from "axios";
import useAPI, { useAPIHelper as fetchAPIData } from "@/services/api/hook";
import { useAuth } from "@/services/auth/AuthContext";
import {
  ComposeFlags,
  ProposalLine,
  ProposalSlotBox,
  ProposalSlotKind
} from "./types";
import type { ResolvedDocument } from "./document/types";

// What contractsapi answers for the account's active proposal template; null
// when the account has none.
export interface ActiveProposalTemplateResponse {
  template: {
    templateId: string;
    versionId: string;
    name: string;
    sections: { id: string; title: string }[];
    signaturePlaces: number;
  } | null;
}

// The CRM field that would fill a data slot the record left empty.
export interface GeneratedSlotMissing {
  record: string;
  recordId: string | null;
  field: string;
}

export interface GeneratedSlot {
  id: string;
  section: string;
  label: string;
  kind: ProposalSlotKind;
  source: string | null;
  record: string | null;
  readOnly: boolean;
  readOnlyNote?: string | null;
  // False on a field the rep types that may go out blank; absent means it may not.
  required?: boolean;
  multiline: boolean;
  // The most characters the rep may type; Save refuses more.
  maxLength?: number | null;
  boxes: ProposalSlotBox[];
  value: string | null;
  missing: GeneratedSlotMissing | null;
}

// A proposal computed from the contract's current terms; nothing is stored.
export interface GeneratedProposal {
  versionId: string;
  proposalName: string;
  sections: { id: string; title: string }[];
  signaturePlaces: number;
  slots: GeneratedSlot[];
  lines: ProposalLine[];
  // The resolved document of a flowing template; null for a fixed-box one.
  document?: ResolvedDocument | null;
}

// A composed document's local URL, and the slots it could not show as given.
export interface ComposedDocument extends ComposeFlags {
  url: string;
}

const CUT_SLOTS_HEADER = "x-proposal-cut-slots";
const UNSUPPORTED_SLOTS_HEADER = "x-proposal-unsupported-slots";

// Each header is a comma-separated list of slot ids, sent only when not empty.
const slotIds = (value: unknown): string[] =>
  typeof value === "string"
    ? value
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)
    : [];

export const composeFlags = (
  headers: AxiosResponseHeaders | undefined
): ComposeFlags => ({
  // AxiosHeaders reads names case-insensitively.
  cutSlotIds: slotIds(headers?.get(CUT_SLOTS_HEADER)),
  unsupportedSlotIds: slotIds(headers?.get(UNSUPPORTED_SLOTS_HEADER))
});

export interface ComposeRequest {
  templateVersionId: string;
  proposalName: string;
  values: Record<string, string | null>;
  lines: ProposalLine[];
  // A flowing template's optional-block choices, by block id.
  included?: Record<string, boolean>;
}

// One operation contractsapi allows on a proposal right now.
export interface ProposalActionResponse {
  name: string;
  uri: string;
  method: string;
}

// One recipient's state at the e-sign vendor, as the last scan read it.
export interface RecipientStateResponse {
  email: string;
  signed: boolean;
  viewedAt?: string | null;
  signedAt?: string | null;
  declinedAt?: string | null;
  // Typed by the signer: plain text, never rendered as HTML.
  declineReason?: string | null;
}

// Signers done out of all signers, and each recipient's state; set once the
// proposal went out.
export interface ProposalSigningResponse {
  signed: number;
  total: number;
  recipients: RecipientStateResponse[];
}

// A saved proposal as the Proposals panel lists it: its latest version's state.
export interface ProposalSummaryResponse {
  id: string;
  name: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  createdBy: { displayName: string; avatarUrl: string };
  outOfDate: boolean;
  signing?: ProposalSigningResponse | null;
  actions: ProposalActionResponse[];
}

// One version of a proposal, with who it went to, where each of them stands,
// the files it went out and came back signed as, and the statuses it went
// through. `author`, `recipients` and `signing` are absent from a contractsapi
// older than them.
export interface ProposalVersionHistoryResponse {
  id: string;
  versionIndex: number;
  status: string;
  author?: { displayName: string; avatarUrl: string };
  createdAt: string;
  lastModifiedAt: string;
  sentFileAttachmentId?: string | null;
  signedFileAttachmentId?: string | null;
  recipients?: { ordered: boolean; list: SavedRecipientResponse[] };
  signing?: ProposalSigningResponse | null;
  // Oldest first.
  history?: { status: string; appliedAt: string }[];
}

// Every version of a proposal, newest first.
export interface ProposalVersionsResponse {
  versions: ProposalVersionHistoryResponse[];
}

// The version a saved proposal shows; a later save sends `id` and
// `lastModifiedAt` back as its `basedOn`.
export interface ProposalVersionResponse {
  id: string;
  versionIndex: number;
  status: string;
  templateVersionId: string;
  lastModifiedAt: string;
}

export interface SavedRecipientResponse {
  key: string;
  personId?: string | null;
  name: string;
  email: string;
  company?: string | null;
  jobTitle?: string | null;
  role: string;
}

// A saved proposal opened in the editor: Generate's shape, with what the rep
// typed on each slot and the recipients they chose.
export interface SavedProposalResponse {
  proposal: ProposalSummaryResponse;
  version: ProposalVersionResponse;
  contractId: string;
  dealId: string | null;
  sections: { id: string; title: string }[];
  signaturePlaces: number;
  slots: (GeneratedSlot & { typedValue: string | null })[];
  lines: ProposalLine[];
  // The resolved document with what the rep typed; null for a fixed-box template.
  document?: ResolvedDocument | null;
  recipients: { ordered: boolean; list: SavedRecipientResponse[] };
  actions: ProposalActionResponse[];
}

export interface SaveRecipientRequest {
  key: string;
  personId?: string;
  name: string;
  email: string;
  company?: string;
  jobTitle?: string;
  role: string;
}

export interface SaveProposalRequest {
  dealId: string;
  templateVersionId: string;
  // First save only.
  proposalId?: string;
  name?: string;
  // What the rep typed, by slot id.
  values: Record<string, string>;
  // A flowing template's optional-block choices, by block id.
  included?: Record<string, boolean>;
  recipients: { ordered: boolean; list: SaveRecipientRequest[] };
  // Every save after the first.
  basedOn?: { versionId: string; lastModifiedAt: string };
  regenerated?: boolean;
}

// The version on screen, as for a save: a draft changed since is not sent.
export interface SendProposalRequest {
  dealId: string;
  basedOn: { versionId: string; lastModifiedAt: string };
}

// The version the rep confirmed: one changed since is not revised.
export type ReviseProposalRequest = SendProposalRequest;

// The version the rep confirmed: one changed since is not withdrawn.
export type WithdrawProposalRequest = SendProposalRequest;

// A refused proposal request. Send, Revise and Withdraw also answer 202 with
// one, code SEND_IN_PROGRESS or WITHDRAW_IN_PROGRESS, while still running.
export interface ProposalRefusalResponse {
  code: string;
  message: string;
}

export const ACTIVE_TEMPLATE_ENDPOINT = "contracts/proposals/templates/active";

export const useActiveProposalTemplate = (
  options: { enabled?: boolean } = {}
) =>
  useAPI(ACTIVE_TEMPLATE_ENDPOINT, undefined, {
    enabled: options.enabled,
    staleTime: Infinity
  }) as Omit<ReturnType<typeof useAPI>, "data"> & {
    data: ActiveProposalTemplateResponse | undefined;
  };

// Generate and compose are reads the editor runs from inside its own queries
// and mutations, so they are plain calls rather than hooks of their own.
export const useProposalsApi = () => {
  const { token, tenantId, tokenExpiry } = useAuth();
  const authRef = useRef({ token, tenantId, tokenExpiry });
  authRef.current = { token, tenantId, tokenExpiry };

  const generate = useCallback(
    (dealId: string): Promise<GeneratedProposal> =>
      fetchAPIData(authRef.current, [
        "contracts/proposals/generate",
        { method: "POST", data: { dealId } }
      ]),
    []
  );

  // The PDF comes back as bytes; the editor shows it from a local URL. Two
  // headers name the slots it could not show as given.
  const compose = useCallback(
    async (
      body: ComposeRequest,
      signal?: AbortSignal
    ): Promise<ComposedDocument> => {
      const { pdf, flags }: { pdf: Blob; flags: ComposeFlags } =
        await fetchAPIData(
          authRef.current,
          [
            "contracts/proposals/compose",
            {
              method: "POST",
              data: body,
              responseType: "blob",
              // A refusal's body is left as it came.
              transformResponse: (
                pdf: Blob,
                headers: AxiosResponseHeaders,
                status?: number
              ) =>
                status !== undefined && status >= 400
                  ? pdf
                  : { pdf, flags: composeFlags(headers) }
            }
          ],
          signal
        );
      return { url: URL.createObjectURL(pdf), ...flags };
    },
    []
  );

  // The deal's saved proposals, newest first; none when it has no contract.
  const list = useCallback(
    async (dealId: string): Promise<ProposalSummaryResponse[]> => {
      const response: { proposals: ProposalSummaryResponse[] } =
        await fetchAPIData(authRef.current, [
          "contracts/proposals",
          { queryParams: { dealId } }
        ]);
      return response.proposals;
    },
    []
  );

  const open = useCallback(
    (proposalId: string): Promise<SavedProposalResponse> =>
      fetchAPIData(authRef.current, [
        "contracts/proposals/proposalById",
        { pathParams: { proposalId } }
      ]),
    []
  );

  // The first save creates the proposal; later ones go to its id.
  const save = useCallback(
    (
      body: SaveProposalRequest,
      proposalId?: string
    ): Promise<SavedProposalResponse> =>
      fetchAPIData(
        authRef.current,
        proposalId
          ? [
              "contracts/proposals/proposalById",
              { method: "PUT", pathParams: { proposalId }, data: body }
            ]
          : ["contracts/proposals", { method: "POST", data: body }]
      ),
    []
  );

  const rename = useCallback(
    (proposalId: string, name: string): Promise<ProposalSummaryResponse> =>
      fetchAPIData(authRef.current, [
        "contracts/proposals/proposalById",
        { method: "PATCH", pathParams: { proposalId }, data: { name } }
      ]),
    []
  );

  const remove = useCallback(
    (proposalId: string): Promise<void> =>
      fetchAPIData(authRef.current, [
        "contracts/proposals/proposalById",
        { method: "DELETE", pathParams: { proposalId } }
      ]),
    []
  );

  // The proposal, now Sent; or, while the Send is still running, its 202.
  const send = useCallback(
    (
      proposalId: string,
      body: SendProposalRequest
    ): Promise<SavedProposalResponse | ProposalRefusalResponse> =>
      fetchAPIData(authRef.current, [
        "contracts/proposals/send",
        { method: "POST", pathParams: { proposalId }, data: body }
      ]),
    []
  );

  // The proposal with its new draft; or, while the withdraw is still
  // running, its 202.
  const revise = useCallback(
    (
      proposalId: string,
      body: ReviseProposalRequest
    ): Promise<SavedProposalResponse | ProposalRefusalResponse> =>
      fetchAPIData(authRef.current, [
        "contracts/proposals/revise",
        { method: "POST", pathParams: { proposalId }, data: body }
      ]),
    []
  );

  // The proposal with its version now Voided and no new draft; or, while the
  // withdraw is still running, its 202.
  const withdraw = useCallback(
    (
      proposalId: string,
      body: WithdrawProposalRequest
    ): Promise<SavedProposalResponse | ProposalRefusalResponse> =>
      fetchAPIData(authRef.current, [
        "contracts/proposals/withdraw",
        { method: "POST", pathParams: { proposalId }, data: body }
      ]),
    []
  );

  const versions = useCallback(
    (proposalId: string): Promise<ProposalVersionsResponse> =>
      fetchAPIData(authRef.current, [
        "contracts/proposals/versions",
        { pathParams: { proposalId } }
      ]),
    []
  );

  // Asks esignconnector to read the vendor now rather than at its next
  // scheduled scan; answers once what it read is stored.
  const refreshSigning = useCallback(
    (): Promise<void> =>
      fetchAPIData(authRef.current, [
        "esign/requests/refresh",
        { method: "POST" }
      ]),
    []
  );

  // A stored file (the one sent, or the one signed), as a local URL.
  const attachmentUrl = useCallback(
    async (attachmentId: string): Promise<string> => {
      const file: Blob = await fetchAPIData(authRef.current, [
        "attachments/id",
        { pathParams: { attachmentId }, responseType: "blob" }
      ]);
      return URL.createObjectURL(file);
    },
    []
  );

  // A flowing template version's file (its background PDF, or a font), as a local URL.
  const templateFile = useCallback(
    async (versionId: string, path: string): Promise<string> => {
      const segments = path.split("/");
      // One directory level at most, and never a step out of the version's files.
      if (
        segments.length > 2 ||
        segments.some((segment) => ["", ".", ".."].includes(segment))
      )
        throw new Error(`Unsupported template file path ${path}`);
      const [directory, file] = segments;
      const blob: Blob = await fetchAPIData(authRef.current, [
        file === undefined
          ? "contracts/proposals/templates/file"
          : "contracts/proposals/templates/fileInDirectory",
        {
          pathParams:
            file === undefined
              ? { versionId, file: directory }
              : { versionId, directory, file },
          responseType: "blob"
        }
      ]);
      return URL.createObjectURL(blob);
    },
    []
  );

  return useMemo(
    () => ({
      generate,
      compose,
      templateFile,
      list,
      open,
      save,
      rename,
      remove,
      send,
      revise,
      withdraw,
      versions,
      refreshSigning,
      attachmentUrl
    }),
    [
      generate,
      compose,
      templateFile,
      list,
      open,
      save,
      rename,
      remove,
      send,
      revise,
      withdraw,
      versions,
      refreshSigning,
      attachmentUrl
    ]
  );
};
